import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, FolderOpen, Euro, Search, ChevronDown, X } from '../icons/wp-icons';
import Layout from '../components/Layout/Layout';
import { createProjekt, getProjektById, updateProjekt } from '../api/projektApi';
import { getAllKunden } from '../api/kundenApi';
import toast from 'react-hot-toast';

const STATUS_OPTIONS = [
  { value: 'PLANUNG', label: 'Planung' },
  { value: 'IN_BEARBEITUNG', label: 'In Bearbeitung' },
  { value: 'ABGESCHLOSSEN', label: 'Abgeschlossen' },
  { value: 'ABGERECHNET', label: 'Abgerechnet' }
];

export default function ProjektForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [kunden, setKunden] = useState([]);
  const [kundenLoading, setKundenLoading] = useState(true);
  const [kundenSuche, setKundenSuche] = useState('');
  const [showKundenDropdown, setShowKundenDropdown] = useState(false);
  const [selectedKunde, setSelectedKunde] = useState(null);
  const kundenDropdownRef = useRef(null);
  const [formData, setFormData] = useState({
    titel: '',
    beschreibung: '',
    notizen: '',
    kundeId: '',
    strasse: '',
    plz: '',
    ort: '',
    startDatum: '',
    endDatum: '',
    status: 'PLANUNG',
    budgetNetto: '' // Budget in Euro (wird in Cent gespeichert)
  });

  useEffect(() => {
    loadKunden();
    if (isEdit) loadProjekt();
  }, [id]);

  // Click-Outside Handler für Kundensuche
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (kundenDropdownRef.current && !kundenDropdownRef.current.contains(event.target)) {
        setShowKundenDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Setze selectedKunde wenn Kunden geladen und kundeId vorhanden (Edit-Modus)
  useEffect(() => {
    if (formData.kundeId && kunden.length > 0 && !selectedKunde) {
      const kunde = kunden.find(k => k.id === formData.kundeId);
      if (kunde) {
        setSelectedKunde(kunde);
      }
    }
  }, [kunden, formData.kundeId]);

  // Gefilterte Kundenliste basierend auf Suche
  const gefilterteKunden = kunden.filter(kunde => {
    const suchText = kundenSuche.toLowerCase();
    return (
      kunde.name.toLowerCase().includes(suchText) ||
      kunde.email?.toLowerCase().includes(suchText) ||
      kunde.telefon?.toLowerCase().includes(suchText)
    );
  });

  const loadKunden = async () => {
    try {
      const data = await getAllKunden();
      setKunden(data);
    } catch (error) {
      toast.error('Fehler beim Laden der Kunden');
    } finally {
      setKundenLoading(false);
    }
  };

  const loadProjekt = async () => {
    try {
      const projekt = await getProjektById(id);
      setFormData({
        titel: projekt.titel || '',
        beschreibung: projekt.beschreibung || '',
        notizen: projekt.notizen || '',
        kundeId: projekt.kundeId || '',
        strasse: projekt.strasse || '',
        plz: projekt.plz || '',
        ort: projekt.ort || '',
        startDatum: projekt.startDatum ? projekt.startDatum.split('T')[0] : '',
        endDatum: projekt.endDatum ? projekt.endDatum.split('T')[0] : '',
        status: projekt.status || 'PLANUNG',
        // Budget von Cent in Euro konvertieren
        budgetNetto: projekt.budgetNetto ? (projekt.budgetNetto / 100).toFixed(2) : ''
      });
    } catch (error) {
      toast.error('Projekt nicht gefunden');
      navigate('/projekte');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.titel || !formData.kundeId) {
      toast.error('Titel und Kunde sind erforderlich');
      return;
    }

    setLoading(true);
    try {
      // Daten für API vorbereiten - Budget von Euro in Cent konvertieren
      const submitData = {
        ...formData,
        budgetNetto: formData.budgetNetto
          ? Math.round(parseFloat(formData.budgetNetto) * 100)
          : null
      };

      if (isEdit) {
        await updateProjekt(id, submitData);
        toast.success('Projekt aktualisiert');
        navigate(`/projekte/${id}`);
      } else {
        const projekt = await createProjekt(submitData);
        toast.success('Projekt erstellt');
        navigate(`/projekte/${projekt.id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  const handleKundeSelect = (kunde) => {
    setSelectedKunde(kunde);
    setFormData(prev => ({
      ...prev,
      kundeId: kunde.id,
      // Optional: Adresse vom Kunden übernehmen wenn noch leer
      strasse: prev.strasse || kunde?.adresse || '',
    }));
    setKundenSuche('');
    setShowKundenDropdown(false);
  };

  const handleKundeRemove = () => {
    setSelectedKunde(null);
    setFormData(prev => ({
      ...prev,
      kundeId: ''
    }));
    setKundenSuche('');
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 max-w-3xl">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(isEdit ? `/projekte/${id}` : '/projekte')}
            className="p-2 hover:bg-surface-elevated rounded-button transition"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <FolderOpen className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
            <h1 className="text-2xl sm:text-3xl font-serif font-bold text-text">
              {isEdit ? 'Projekt bearbeiten' : 'Neues Projekt'}
            </h1>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-surface rounded-card shadow-card p-6 space-y-4">
            <h2 className="font-semibold text-lg text-text mb-4">Grunddaten</h2>

            <div>
              <label className="block text-sm font-medium text-text mb-1">Titel *</label>
              <input
                type="text"
                value={formData.titel}
                onChange={(e) => setFormData({ ...formData, titel: e.target.value })}
                className="w-full px-4 py-2 border border-border rounded-button bg-surface focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="z.B. Fassadensanierung Müller"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text mb-1">Kunde *</label>
              <div className="relative" ref={kundenDropdownRef}>
                {selectedKunde ? (
                  <div className="flex items-center justify-between w-full px-4 py-2 border border-border rounded-button bg-surface-elevated">
                    <div className="min-w-0 flex-1">
                      <span className="font-medium block truncate">{selectedKunde.name}</span>
                      {selectedKunde.email && (
                        <span className="text-text-muted text-sm block truncate">{selectedKunde.email}</span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleKundeRemove}
                      className="p-1.5 hover:bg-surface rounded transition ml-2 shrink-0"
                    >
                      <X className="w-4 h-4 text-text-muted" />
                    </button>
                  </div>
                ) : kundenLoading ? (
                  <div className="w-full px-4 py-2 border border-border rounded-button bg-surface-elevated text-text-muted">
                    Lade Kunden...
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-muted" />
                    <input
                      type="text"
                      value={kundenSuche}
                      onChange={(e) => {
                        setKundenSuche(e.target.value);
                        setShowKundenDropdown(true);
                      }}
                      onFocus={() => setShowKundenDropdown(true)}
                      placeholder="Kunde suchen..."
                      className="w-full pl-10 pr-10 py-2 border border-border rounded-button bg-surface focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => setShowKundenDropdown(!showKundenDropdown)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    >
                      <ChevronDown className={`w-5 h-5 text-text-muted transition-transform ${showKundenDropdown ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                )}

                {/* Dropdown Liste */}
                {showKundenDropdown && !selectedKunde && !kundenLoading && (
                  <div className="absolute z-10 w-full mt-1 bg-surface border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {gefilterteKunden.length === 0 ? (
                      <div className="px-4 py-3 text-text-muted text-center">
                        {kundenSuche ? 'Kein Kunde gefunden' : 'Keine Kunden vorhanden'}
                      </div>
                    ) : (
                      gefilterteKunden.map(kunde => (
                        <button
                          key={kunde.id}
                          type="button"
                          onClick={() => handleKundeSelect(kunde)}
                          className="w-full px-4 py-3 text-left hover:bg-surface-elevated border-b border-border last:border-b-0 transition"
                        >
                          <div className="font-medium">{kunde.name}</div>
                          <div className="text-sm text-text-muted flex gap-3">
                            {kunde.email && <span>{kunde.email}</span>}
                            {kunde.telefon && <span>{kunde.telefon}</span>}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {isEdit && (
              <div>
                <label className="block text-sm font-medium text-text mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-button bg-surface focus:ring-2 focus:ring-primary"
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-text mb-1">Beschreibung</label>
              <textarea
                value={formData.beschreibung}
                onChange={(e) => setFormData({ ...formData, beschreibung: e.target.value })}
                className="w-full px-4 py-2 border border-border rounded-button bg-surface focus:ring-2 focus:ring-primary"
                rows={3}
                placeholder="Projektbeschreibung..."
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text mb-1">Startdatum</label>
                <input
                  type="date"
                  value={formData.startDatum}
                  onChange={(e) => setFormData({ ...formData, startDatum: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-button bg-surface focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Enddatum</label>
                <input
                  type="date"
                  value={formData.endDatum}
                  onChange={(e) => setFormData({ ...formData, endDatum: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-button bg-surface focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-card shadow-card p-6 space-y-4">
            <h2 className="font-semibold text-lg text-text mb-4 flex items-center gap-2">
              <Euro className="w-5 h-5 text-primary" />
              Budget
            </h2>
            <p className="text-sm text-text-muted mb-4">
              Legen Sie ein geplantes Budget für das Projekt fest. Dies hilft bei der Kostenkontrolle und ermöglicht Vergleiche zwischen Planung und tatsächlichen Kosten.
            </p>

            <div>
              <label className="block text-sm font-medium text-text mb-1">Geplantes Budget (Netto)</label>
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.budgetNetto}
                  onChange={(e) => setFormData({ ...formData, budgetNetto: e.target.value })}
                  className="w-full px-4 py-2 pr-12 border border-border rounded-button bg-surface focus:ring-2 focus:ring-primary"
                  placeholder="0,00"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted">€</span>
              </div>
              <p className="text-xs text-text-muted mt-1">
                Netto-Betrag ohne MwSt. Leer lassen wenn kein Budget festgelegt werden soll.
              </p>
            </div>
          </div>

          <div className="bg-surface rounded-card shadow-card p-6 space-y-4">
            <h2 className="font-semibold text-lg text-text mb-4">Projekt-Adresse</h2>
            <p className="text-sm text-text-muted mb-4">
              Die Adresse des Projekts kann von der Kundenadresse abweichen (z.B. bei Baustellen).
            </p>

            <div>
              <label className="block text-sm font-medium text-text mb-1">Straße</label>
              <input
                type="text"
                value={formData.strasse}
                onChange={(e) => setFormData({ ...formData, strasse: e.target.value })}
                className="w-full px-4 py-2 border border-border rounded-button bg-surface focus:ring-2 focus:ring-primary"
                placeholder="Musterstraße 123"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text mb-1">PLZ</label>
                <input
                  type="text"
                  value={formData.plz}
                  onChange={(e) => setFormData({ ...formData, plz: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-button bg-surface focus:ring-2 focus:ring-primary"
                  placeholder="12345"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Ort</label>
                <input
                  type="text"
                  value={formData.ort}
                  onChange={(e) => setFormData({ ...formData, ort: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-button bg-surface focus:ring-2 focus:ring-primary"
                  placeholder="Musterstadt"
                />
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-card shadow-card p-6">
            <h2 className="font-semibold text-lg text-text mb-4">Interne Notizen</h2>
            <textarea
              value={formData.notizen}
              onChange={(e) => setFormData({ ...formData, notizen: e.target.value })}
              className="w-full px-4 py-2 border border-border rounded-button bg-surface focus:ring-2 focus:ring-primary"
              rows={4}
              placeholder="Interne Notizen zum Projekt (nicht für den Kunden sichtbar)..."
            />
          </div>

          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate(isEdit ? `/projekte/${id}` : '/projekte')}
              className="px-6 py-2 border border-border rounded-button hover:bg-surface-elevated transition"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2 bg-primary text-white rounded-button font-medium hover:bg-primary-dark disabled:opacity-50 transition"
            >
              <Save className="w-5 h-5" />
              {loading ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
